import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { getUser, getToken } from "./lib/api";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import Refund from "./pages/Refund";
import About from "./pages/About";
import Contact from "./pages/Contact";
import HelpCenter from "./pages/HelpCenter";
import SellerDashboard from "./pages/SellerDashboard";
import UploadPage from "./pages/Upload";
import Reports from "./pages/Reports";
import Insights from "./pages/Insights";
import Catalogue from "./pages/Catalogue";
import GrowthAI from "./pages/GrowthAI";
import AutoListing from "./pages/AutoListing";
import AutoListingQuick from "./pages/AutoListingQuick";
import GrowthAnalytics from "./pages/GrowthAnalytics";
import MyntraHub from "./pages/MyntraHub";
import PublicCatalog from "./pages/PublicCatalog";
import SellerProfile from "./pages/SellerProfile";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import MarketplaceLanding from "./pages/MarketplaceLanding";
import AdminOverview from "./pages/AdminOverview";
import AdminSellers from "./pages/AdminSellers";
import AdminMarketplace from "./pages/AdminMarketplace";
import AdminTemplates from "./pages/AdminTemplates";
import AuthCallback from "./pages/AuthCallback";
import GoogleCallback from "./pages/GoogleCallback";
import AdminSettings from "./pages/AdminSettings";
import AdminPayments from "./pages/AdminPayments";
import Billing from "./pages/Billing";
import PaymentResult from "./pages/PaymentResult";
import MyntraConnect from "./pages/MyntraConnect";
import MyntraSync from "./pages/MyntraSync";
import TrialSignup from "./pages/TrialSignup";
import BrandDirectory from "./pages/BrandDirectory";
import BrandWall from "./pages/BrandWall";
import BbiHowItWorks from "./pages/BbiHowItWorks";
import BbiPricingPage from "./pages/BbiPricingPage";
import BrandProfile from "./pages/BrandProfile";
import BrandArticle from "./pages/BrandArticle";
import BrandLogin from "./pages/BrandLogin";
import BrandPortal from "./pages/BrandPortal";
import AdminBrands from "./pages/AdminBrands";
import RenewalModal from "./components/RenewalModal";
import TierGate from "./components/TierGate";

function Protected({ children, role }) {
  const token = getToken();
  const user = getUser();
  if (!token || !user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/seller"} replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster richColors position="top-right" theme="dark" />
        <RenewalModal />
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

function AppRouter() {
  // Emergent Google OAuth returns to {origin}/...#session_id=... — detect the
  // fragment SYNCHRONOUSLY during render (useLocation, not window.location)
  // so the callback runs before any protected-route auth check.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<TrialSignup />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* SEO/GEO marketplace landing pages — explicit routes so React Router
             reliably matches the `ai-catalog-{marketplace}` URL pattern. */}
          <Route path="/ai-catalog-myntra" element={<MarketplaceLanding />} />
          <Route path="/ai-catalog-ajio" element={<MarketplaceLanding />} />
          <Route path="/ai-catalog-nykaa" element={<MarketplaceLanding />} />
          <Route path="/ai-catalog-amazon" element={<MarketplaceLanding />} />
          <Route path="/ai-catalog-flipkart" element={<MarketplaceLanding />} />
          <Route path="/ai-catalog-meesho" element={<MarketplaceLanding />} />
          <Route path="/catalog/:slug" element={<PublicCatalog />} />
          <Route path="/brands" element={<BrandDirectory />} />
          <Route path="/brands/all" element={<BrandWall />} />
          <Route path="/brands/how-it-works" element={<BbiHowItWorks />} />
          <Route path="/brands/pricing" element={<BbiPricingPage />} />
          <Route path="/brands/:slug" element={<BrandProfile />} />
          <Route path="/brands/:slug/:articleSlug" element={<BrandArticle />} />
          <Route path="/brand-login" element={<BrandLogin />} />
          <Route path="/brand-portal" element={<BrandPortal />} />
          <Route path="/auth/google" element={<GoogleCallback />} />

          <Route path="/seller" element={<Protected role="seller"><TierGate tierKey="dashboard"><SellerDashboard /></TierGate></Protected>} />
          <Route path="/seller/analytics" element={<Protected role="seller"><TierGate tierKey="analytics"><GrowthAnalytics /></TierGate></Protected>} />
          <Route path="/seller/upload" element={<Protected role="seller"><TierGate tierKey="upload"><UploadPage /></TierGate></Protected>} />
          <Route path="/seller/reports" element={<Protected role="seller"><TierGate tierKey="reports"><Reports /></TierGate></Protected>} />
          <Route path="/seller/insights" element={<Protected role="seller"><TierGate tierKey="insights"><Insights /></TierGate></Protected>} />
          <Route path="/seller/catalogue" element={<Protected role="seller"><TierGate tierKey="catalogue"><Catalogue /></TierGate></Protected>} />
          <Route path="/seller/growth" element={<Protected role="seller"><TierGate tierKey="growth"><GrowthAI /></TierGate></Protected>} />
          <Route path="/seller/auto-listing" element={<Protected role="seller"><TierGate tierKey="auto_listing"><AutoListingQuick /></TierGate></Protected>} />
          <Route path="/seller/myntra" element={<Protected role="seller"><MyntraHub /></Protected>} />
          <Route path="/seller/auto-listing/advanced" element={<Protected role="seller"><TierGate tierKey="auto_listing"><AutoListing /></TierGate></Protected>} />
          <Route path="/seller/profile" element={<Protected role="seller"><TierGate tierKey="profile"><SellerProfile /></TierGate></Protected>} />
          <Route path="/seller/billing" element={<Protected role="seller"><Billing /></Protected>} />
          <Route path="/payment/success" element={<Protected role="seller"><PaymentResult kind="success" /></Protected>} />
          <Route path="/payment/failed" element={<Protected role="seller"><PaymentResult kind="failed" /></Protected>} />
          <Route path="/payment/pending" element={<Protected role="seller"><PaymentResult kind="pending" /></Protected>} />
          <Route path="/seller/integrations/myntra" element={<Protected role="seller"><TierGate tierKey="dashboard"><MyntraConnect /></TierGate></Protected>} />
          <Route path="/seller/integrations/myntra/sync" element={<Protected role="seller"><TierGate tierKey="dashboard"><MyntraSync /></TierGate></Protected>} />

          <Route path="/admin" element={<Protected role="admin"><AdminOverview /></Protected>} />
          <Route path="/admin/sellers" element={<Protected role="admin"><AdminSellers /></Protected>} />
          <Route path="/admin/marketplace" element={<Protected role="admin"><AdminMarketplace /></Protected>} />
          <Route path="/admin/templates" element={<Protected role="admin"><AdminTemplates /></Protected>} />
          <Route path="/admin/brands" element={<Protected role="admin"><AdminBrands /></Protected>} />
          <Route path="/admin/payments" element={<Protected role="admin"><AdminPayments /></Protected>} />
          <Route path="/admin/settings" element={<Protected role="admin"><AdminSettings /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}
